import { useEffect, useState } from "react";
import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { Building2, Check, RotateCcw, ShieldCheck, Share2, Truck, ArrowLeft } from "lucide-react";
import plusIcon from "@/assets/plus-icon.png.asset.json";
import minusIcon from "@/assets/minus-icon.png.asset.json";
import besteKoopBadge from "@/assets/beste-koop-badge.png.asset.json";
import { toast } from "sonner";
import { normalizeSpecs } from "@/lib/product-specs";
import { prioritizedPros } from "@/lib/product-pros";
import {
  productBySlug,
  categoryBySlug,
  subcategoryBySlug,
  relatedProducts,
  formatEuro,
  applyDiscount,
  discountPercent,
  type Product,
} from "@/lib/catalog";
import { accessorySlugsFor, frequentlyBoughtSlugsFor } from "@/lib/product-accessories";
import { breadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { SHIPPING_POLICY_TEXT, PRICE_ON_REQUEST, PRICE_ON_REQUEST_LABEL } from "@/lib/commerce";

import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { ProductGallery } from "@/components/shop/ProductGallery";
import { ProductCard } from "@/components/shop/ProductCard";
import { CameraFeatureIcons } from "@/components/shop/CameraFeatureIcons";
import { PaymentMethods } from "@/components/shop/PaymentMethods";
import { ProductReviewsBlock } from "@/components/shop/ProductReviewsBlock";
import { useAuth } from "@/hooks/use-auth";
import { useVat, priceForDisplay } from "@/hooks/use-vat";
import { useTrackRecentlyViewed, useRecentlyViewed } from "@/hooks/use-recently-viewed";
import { useCatalogOverrides, withDbOverrides } from "@/hooks/use-price-overrides";
import { toIncl } from "@/lib/totals";
import { offerShippingDetails, STANDARD_SHIPPING_INCL_CENTS } from "@/lib/shipping-jsonld";

/** NL-basistarief incl. btw (zonder gratis-verzendingsdrempel) voor JSON-LD. */
function baseShippingInclCents(shippingExCents?: number | null) {
  if (shippingExCents !== null && shippingExCents !== undefined) return toIncl(shippingExCents);
  return STANDARD_SHIPPING_INCL_CENTS;
}

function boldModelInText(text: string, mpn?: string) {
  if (!mpn) return text;

  const variants = new Set<string>([mpn]);
  for (const prefix of ["DHI-", "DH-", "DS-"]) {
    if (mpn.startsWith(prefix)) {
      const rest = mpn.slice(prefix.length);
      variants.add(rest);
      if (rest.startsWith("IPC-")) {
        variants.add(rest.slice(4));
      }
    }
  }

  const sorted = Array.from(variants).sort((a, b) => b.length - a.length);
  const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(
    `\\b(${sorted.map(escape).join("|")})\\b`,
    "g"
  );
  const parts = text.split(pattern);

  return (
    <>
      {parts.map((part, idx) =>
        sorted.includes(part) ? (
          <strong key={idx} className="font-semibold text-foreground">
            {part}
          </strong>
        ) : (
          part
        )
      )}
    </>
  );
}


export const Route = createFileRoute("/product/$slug")({
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    tab?: "desc" | "faq";
    qty?: number;
  } => {
    const rawTab = search.tab;
    const tab: "desc" | "faq" | undefined =
      rawTab === "faq" || rawTab === "desc" ? rawTab : undefined;
    const qtyNum = Number(search.qty);
    const qty = Number.isFinite(qtyNum) && qtyNum >= 1 ? Math.min(99, Math.floor(qtyNum)) : undefined;
    return { tab, qty };
  },
  // Loader gebruikt @/lib/catalog (~250 KB); groepeer met de component
  // zodat catalog uit de main bundle blijft.
  codeSplitGroupings: [["loader", "component", "notFoundComponent", "errorComponent"]],
  loader: async ({ params }) => {
    const product = productBySlug(params.slug);
    if (!product) throw notFound();
    const cat = categoryBySlug(product.category);
    const sub = cat && product.subcategory ? subcategoryBySlug(cat, product.subcategory) : undefined;
    try {
      const { listPublicPrices } = await import("@/lib/public-prices.functions");
      const prices = await listPublicPrices();
      const match = prices.find((p) => p.slug === product.slug);
      if (match && match.price_cents > 0) {
        return {
          product: {
            ...product,
            list_price_cents: match.price_cents,
          },
          cat,
          sub,
        };
      }
    } catch {
      // fall back to static catalog price
    }
    return { product, cat, sub };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return {};
    const p = loaderData.product;
    const title = (p.meta_title ?? `${p.name} kopen`).slice(0, 38).trimEnd();
    let description = (p.meta_description ?? p.short_description).slice(0, 160);
    if (description.length < 130) {
      const suffix = ` — ${p.brand} bij Elvion Group Nederland: duidelijke productinformatie, veilige betaling en vakkundig advies van onze specialisten.`;
      description = `${description}${suffix}`.slice(0, 160);
    }
    const url = `https://elvion-group.nl/product/${params.slug}`;
    // Google (Product-structured data, OG en Twitter) eist absolute URL's.
    const image = p.image_url
      ? p.image_url.startsWith("http")
        ? p.image_url
        : `https://elvion-group.nl${p.image_url.startsWith("/") ? "" : "/"}${p.image_url}`
      : undefined;
    // Prijs inclusief BTW (Merchant Center NL vereist tax-inclusive prices).
    const priceInclCents = toIncl(p.list_price_cents);
    const priceEuro = (priceInclCents / 100).toFixed(2);
    // Prijs is geldig t/m einde volgend jaar zodat de feed altijd een
    // priceValidUntil in de toekomst heeft (Google eist future date).
    const now = new Date();
    const priceValidUntil = `${now.getUTCFullYear() + 1}-12-31`;
    const shippingCents = (p as unknown as { shipping_cents?: number | null }).shipping_cents;
    const weightGrams = (p as unknown as { weight_grams?: number | null }).weight_grams;
    const jsonLd = {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: p.name,
      description: p.short_description,
      sku: p.mpn ?? p.slug,
      mpn: p.mpn,
      productID: p.gtin ?? p.mpn ?? p.slug,
      ...(p.gtin ? { gtin: p.gtin } : {}),
      brand: { "@type": "Brand", name: p.brand },
      ...(image ? { image } : {}),
      ...(weightGrams
        ? { weight: { "@type": "QuantitativeValue", value: weightGrams, unitCode: "GRM" } }
        : {}),
      offers: {
        "@type": "Offer",
        url,
        priceCurrency: "EUR",
        price: priceEuro,
        priceValidUntil,
        itemCondition: `https://schema.org/${
          p.condition === "refurbished"
            ? "RefurbishedCondition"
            : p.condition === "used"
              ? "UsedCondition"
              : "NewCondition"
        }`,
        availability: `https://schema.org/${
          (p.availability ?? (p.stock === "out" ? "out_of_stock" : "in_stock")) === "out_of_stock"
            ? "OutOfStock"
            : "InStock"
        }`,
        seller: { "@type": "Organization", name: "Elvion Group Nederland" },
        shippingDetails: offerShippingDetails(baseShippingInclCents(shippingCents)),
        eligibleRegion: [
          { "@type": "Country", name: "Nederland" },
          { "@type": "Country", name: "België" },
        ],
        hasMerchantReturnPolicy: {
          "@type": "MerchantReturnPolicy",
          applicableCountry: ["NL", "BE"],
          returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
          merchantReturnDays: 14,
          returnMethod: "https://schema.org/ReturnByMail",
          returnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
        },
      },
    };
    return {
      meta: [
        { title: `${title} | Elvion Group Nederland` },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "product" },
        { property: "og:url", content: url },
        ...(image ? [{ property: "og:image", content: image }] : []),
        { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...(image ? [{ name: "twitter:image", content: image }] : []),
        { name: "product:price:amount", content: priceEuro },
        { name: "product:price:currency", content: "EUR" },
        { name: "product:brand", content: p.brand },
        ...(p.mpn ? [{ name: "product:retailer_item_id", content: p.mpn }] : []),
        ...(p.gtin ? [{ property: "product:gtin", content: p.gtin }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbJsonLd([
          { name: "Assortiment", path: "/assortiment" },
          { name: p.name, path: `/product/${p.slug}` },
        ]),
        {
          type: "application/ld+json",
          children: JSON.stringify(jsonLd),
        },
        ...(p.faqs && p.faqs.length > 0
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: p.faqs.map((f: { q: string; a: string }) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }),
              },
            ]
          : []),
      ],
    };
  },
  component: ProductPage,
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl p-10 text-center">
      <h1 className="text-2xl font-bold">Product niet gevonden</h1>
      <Link to="/c" className="mt-4 inline-block text-orange hover:underline">
        Terug naar webshop
      </Link>
    </main>
  ),
  errorComponent: ({ error }) => (
    <main className="mx-auto max-w-3xl p-10 text-center">
      <h1 className="text-2xl font-bold">Er ging iets mis</h1>
      <p className="mt-2 text-text-secondary">{error instanceof Error ? error.message : "Onbekende fout"}</p>
    </main>
  ),
});

function ProductPage() {
  const { product: rawProduct, cat, sub } = Route.useLoaderData();
  const search = Route.useSearch();
  const overrides = useCatalogOverrides();
  const product = withDbOverrides(rawProduct, overrides);
  const { session, profile } = useAuth();
  const { inclVat } = useVat();
  const [qty, setQty] = useState<number>(search.qty ?? 1);
  const [tab, setTab] = useState<"desc" | "faq">(search.tab ?? "desc");
  useTrackRecentlyViewed(product.slug);

  const approved = profile?.verification_status === "approved";
  const discountPct = approved ? Number(profile?.discount_percentage ?? 0) : 0;
  const finalPriceEx = applyDiscount(product.list_price_cents, discountPct);
  const displayPrice = priceForDisplay(finalPriceEx, inclVat);
  const showPrice = true;
  const pctOff = discountPercent(product);

  const related = relatedProducts(product);

  const accessories = accessorySlugsFor(product.slug)
    .map((s) => productBySlug(s))
    .filter((p): p is Product => !!p);

  const frequentlyBought = frequentlyBoughtSlugsFor(product.slug)
    .map((s) => productBySlug(s))
    .filter((p): p is Product => !!p);


  const recentSlugs = useRecentlyViewed();
  const recentProducts = recentSlugs
    .map((s) => productBySlug(s))
    .filter((p): p is Product => !!p && p.slug !== product.slug)
    .slice(0, 4);

  const navigate = useNavigate();

  const handleShare = async () => {
    const params = new URLSearchParams();
    if (qty > 1) params.set("qty", String(qty));
    if (tab !== "desc") params.set("tab", tab);
    const qs = params.toString();
    const url = `${window.location.origin}/product/${product.slug}${qs ? `?${qs}` : ""}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: product.name, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link gekopieerd naar klembord");
      }
    } catch {
      // gebruiker annuleerde delen
    }
  };


  return (
    <>
      <Header variant="shop" />
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate({ to: "/assortiment" })}
          className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-text-secondary hover:text-foreground md:hidden"
        >
          <ArrowLeft className="size-4" />
          Terug
        </button>

        <div className="grid gap-8 lg:grid-cols-2">
          {/* Image */}
          <div className="min-w-0 lg:flex lg:flex-col">
            <div className="relative overflow-hidden rounded-2xl border border-border bg-white lg:flex lg:flex-1 lg:flex-col">
              {pctOff > 0 && !PRICE_ON_REQUEST && (
                <span className="absolute right-3 top-3 z-10 rounded-md bg-[var(--orange)] px-2 py-0.5 text-xs font-bold text-white">
                  -{pctOff}%
                </span>
              )}
              {product.slug === "hikvision-ds-2cd2387g2h-liu" && (
                <img
                  src={besteKoopBadge.url}
                  alt="Beste koop"
                  className="absolute left-16 top-3 z-10 h-14 w-auto sm:left-24 sm:top-4 sm:h-16"
                  loading="eager"
                />
              )}
              <div className="p-3 sm:p-4 lg:flex lg:flex-1 lg:flex-col">
                <ProductGallery product={product} />
              </div>
            </div>
            <div className="hidden w-full overflow-hidden lg:block">
              <CameraFeatureIcons product={product} />
            </div>
          </div>

          {/* Info */}
          <div className="flex flex-col lg:h-full">
            <div className="flex items-start justify-between gap-4">
              <h1 className="text-2xl font-bold leading-tight sm:text-3xl">{product.name}</h1>
              <button
                type="button"
                onClick={handleShare}
                aria-label="Delen"
                className="rounded-full border border-border p-2 text-text-secondary hover:text-foreground"
              >
                <Share2 className="size-4" />
              </button>
            </div>

            {PRICE_ON_REQUEST ? (
              <div className="mt-4 flex flex-col gap-1">
                <span className="text-2xl font-extrabold text-orange">
                  {PRICE_ON_REQUEST_LABEL}
                </span>
                <span className="text-xs text-text-secondary">
                  Vraag een offerte aan voor de actuele prijs.
                </span>
              </div>
            ) : showPrice ? (
              <div className="mt-4 flex flex-col gap-1">
                <div className="flex items-baseline gap-3">
                  {product.old_price_cents && (
                    <span
                      className="text-base text-text-secondary line-through"
                      title="Eerdere adviesprijs / prijs bij losse aanschaf van de onderdelen"
                    >
                      {formatEuro(priceForDisplay(product.old_price_cents, inclVat))}
                    </span>
                  )}
                  <span className="text-3xl font-extrabold text-orange">
                    {formatEuro(displayPrice)}
                  </span>
                  {discountPct > 0 && (
                    <span className="ml-2 rounded bg-orange/10 px-2 py-0.5 text-xs font-bold text-orange">
                      -{discountPct}% accountkorting
                    </span>
                  )}
                </div>
                <span className="text-xs text-text-secondary">
                  {inclVat ? "Incl. 21% btw" : "Excl. btw"}
                </span>
              </div>
            ) : (
              <div className="mt-4">
                <Link
                  to="/auth"
                  className="text-lg font-bold text-orange underline underline-offset-4"
                >
                  Inloggen voor prijs
                </Link>
              </div>
            )}

            <ul className="mt-4 space-y-1.5 text-sm">
              <li className="flex items-center gap-2">
                <Check className="size-4 text-grass" /> Alleen binnen Nederland gratis verzending vanaf €150 incl. btw, daaronder €6,95
              </li>
              <li className="flex items-center gap-2">
                <Check className="size-4 text-grass" /> Ophalen in onze winkel in Wageningen
              </li>
              <li className="flex items-center gap-2">
                <Check className="size-4 text-grass" /> Telefonisch support beschikbaar
              </li>
              {product.lead_time ? (
                <li className="flex items-center gap-2 font-medium text-amber-600">
                  <span className="inline-block size-2 rounded-full bg-amber-500" />
                  {product.lead_time}
                </li>
              ) : (
                <li className="flex items-center gap-2 font-medium text-emerald-600">
                  <span className="inline-block size-2 rounded-full bg-emerald-500" />
                  Op voorraad — levertijd 1–2 werkdagen
                </li>
              )}
            </ul>

            <div className="mt-6 rounded-lg border border-orange/30 bg-orange/5 p-4">
              <p className="text-sm font-semibold text-foreground">Offerte op maat</p>
              <p className="mt-1 text-sm text-text-secondary">Wij voeren geen webshop meer; vraag een vrijblijvende offerte aan of neem contact op.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link to="/offerte-aanvragen" search={{ product: product.slug }}>
                  <Button size="lg" className="bg-[var(--orange)] text-white hover:bg-[var(--orange-hover)]">
                    Offerte aanvragen
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline">Contact opnemen</Button>
                </Link>
              </div>
            </div>

            <p className="mt-5 pt-2 text-sm text-text-secondary">
              Betaal <span className="font-semibold text-foreground">veilig</span> en makkelijk met
              WERO / iDEAL, Creditcard of Betalen bij plaatsing / afhalen
            </p>
            <PaymentMethods className="mt-2" />

            <section className="mt-5 rounded-xl border border-border bg-muted/30 p-4" aria-labelledby="merchant-trust-title">
              <h2 id="merchant-trust-title" className="text-sm font-bold text-foreground">
                Veilig kopen bij Elvion Group Nederland
              </h2>
              <ul className="mt-3 space-y-2 text-xs leading-relaxed text-text-secondary">
                <li className="flex gap-2">
                  <Building2 className="mt-0.5 size-4 shrink-0 text-orange" />
                  <span>Verkoper: Elvion Group Nederland, Betsy van Goorstraat 15, 6702DC Wageningen · KvK 98117475 · BTW NL868363753B01.</span>
                </li>
                <li className="flex gap-2">
                  <ShieldCheck className="mt-0.5 size-4 shrink-0 text-orange" />
                  <span>SSL-beveiligde checkout en Nederlandse klantenservice.</span>
                </li>
                <li className="flex gap-2">
                  <Truck className="mt-0.5 size-4 shrink-0 text-orange" />
                  <span>{SHIPPING_POLICY_TEXT}</span>
                </li>
                <li className="flex gap-2">
                  <RotateCcw className="mt-0.5 size-4 shrink-0 text-orange" />
                  <span>14 dagen bedenktijd en wettelijke garantie volgens Nederlands consumentenrecht.</span>
                </li>
              </ul>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold">
                <Link to="/retourbeleid" className="text-orange underline-offset-4 hover:underline">Retourbeleid</Link>
                <Link to="/verzendbeleid" className="text-orange underline-offset-4 hover:underline">Verzending</Link>
                <Link to="/garantie" className="text-orange underline-offset-4 hover:underline">Garantie</Link>
                <Link to="/voorwaarden" className="text-orange underline-offset-4 hover:underline">Voorwaarden</Link>
              </div>
            </section>

            {/* Accessoires mee bestellen — direct onder betaalmethoden */}
            {accessories.length > 0 && (
              <div className="mt-6 border-t border-border pt-5">
                <h2 className="text-sm font-bold">Accessoires mee bestellen</h2>
                <div className="mt-3 space-y-2">
                  {accessories.map((acc) => {
                    const accFinal = applyDiscount(acc.list_price_cents, discountPct);
                    const accDisplay = priceForDisplay(accFinal, inclVat);
                    return (
                      <div
                        key={acc.slug}
                        className="flex items-center gap-3 rounded-xl border border-border bg-white p-2.5"
                      >
                        <Link
                          to="/product/$slug"
                          params={{ slug: acc.slug }}
                          className="size-14 shrink-0 overflow-hidden rounded-lg border border-border bg-white"
                        >
                          {acc.image_url && (
                            <img
                              src={acc.image_url}
                              alt={acc.name}
                              className="h-full w-full object-contain"
                              loading="lazy"
                            />
                          )}
                        </Link>
                        <div className="min-w-0 flex-1">
                          <Link
                            to="/product/$slug"
                            params={{ slug: acc.slug }}
                            className="line-clamp-2 text-xs font-medium hover:underline"
                          >
                            {acc.name}
                          </Link>
                          <div className="mt-0.5 text-sm font-semibold">
                            {PRICE_ON_REQUEST ? (
                              PRICE_ON_REQUEST_LABEL
                            ) : (
                              <>
                                {formatEuro(accDisplay)}
                                <span className="ml-1 text-[10px] font-normal text-text-secondary">
                                  {inclVat ? "incl." : "excl."} btw
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Veel kochten ook — direct onder betaalmethodes/accessoires */}
            {frequentlyBought.length > 0 && (
              <div className="mt-6 border-t border-border pt-5">
                <h2 className="text-sm font-bold">Veel kochten ook</h2>
                <div className="mt-3 space-y-2">
                  {frequentlyBought.map((fb) => {
                    const fbFinal = applyDiscount(fb.list_price_cents, discountPct);
                    const fbDisplay = priceForDisplay(fbFinal, inclVat);
                    return (
                      <div
                        key={fb.slug}
                        className="flex items-center gap-3 rounded-xl border border-border bg-white p-2.5"
                      >
                        <Link
                          to="/product/$slug"
                          params={{ slug: fb.slug }}
                          className="shrink-0"
                        >
                          <div className="size-14 overflow-hidden rounded-lg border border-border bg-white">
                            {fb.image_url && (
                              <img
                                src={fb.image_url}
                                alt={fb.name}
                                className="h-full w-full object-contain"
                                loading="lazy"
                              />
                            )}
                          </div>
                        </Link>
                        <div className="min-w-0 flex-1">
                          <Link
                            to="/product/$slug"
                            params={{ slug: fb.slug }}
                            className="line-clamp-2 text-xs font-medium hover:underline"
                          >
                            {fb.name}
                          </Link>
                          <div className="mt-0.5 text-sm font-semibold">
                            {PRICE_ON_REQUEST ? (
                              PRICE_ON_REQUEST_LABEL
                            ) : (
                              <>
                                {formatEuro(fbDisplay)}
                                <span className="ml-1 text-[10px] font-normal text-text-secondary">
                                  {inclVat ? "incl." : "excl."} btw
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        </div>


        <div className="mt-8 lg:hidden">
          <CameraFeatureIcons product={product} />
        </div>

        <ProductReviewsBlock className="mt-8" productSlug={product.slug} />




        {/* Pros, cons, specs */}
        <div className="mt-12 grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="text-lg font-bold">Pluspunten</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {prioritizedPros(product.pros, 4).map((p: string) => (
                <li key={p} className="flex items-start gap-2">
                  <img src={plusIcon.url} alt="+" className="mt-0.5 size-5 shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
            {product.cons.length > 0 && (
              <>
                <h2 className="mt-6 text-lg font-bold">Minpunten</h2>
                <ul className="mt-3 space-y-2 text-sm">
                  {product.cons.map((c: string) => (
                    <li key={c} className="flex items-start gap-2">
                      <img src={minusIcon.url} alt="-" className="mt-0.5 size-5 shrink-0" />
                      {c}
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="mt-8 border-b border-border">
              <div className="flex gap-6 text-sm font-semibold">
                <button
                  className={`-mb-px border-b-2 pb-2 ${tab === "desc" ? "border-orange text-orange" : "border-transparent text-text-secondary"}`}
                  onClick={() => setTab("desc")}
                >
                  Beschrijving
                </button>
                <button
                  className={`-mb-px border-b-2 pb-2 ${tab === "faq" ? "border-orange text-orange" : "border-transparent text-text-secondary"}`}
                  onClick={() => setTab("faq")}
                >
                  Veelgestelde vragen
                </button>
              </div>
              <div className="py-4 text-sm leading-relaxed text-text-secondary">
                {tab === "desc" ? (
                  <div className="space-y-3">
                    {product.long_description
                      .split(/\n\n+/)
                      .filter(Boolean)
                      .map((para: string, i: number) => (
                        <p key={i}>{boldModelInText(para, product.mpn)}</p>
                      ))}
                  </div>
                ) : product.faqs && product.faqs.length > 0 ? (
                  <dl className="space-y-4">
                    {product.faqs.map((f: { q: string; a: string }) => (
                      <div key={f.q}>
                        <dt className="font-semibold text-foreground">{f.q}</dt>
                        <dd className="mt-1">{f.a}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p>Heeft u een vraag over dit product? Neem contact op met onze klantenservice.</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-bold">Specificaties</h2>
            <div className="mt-3 overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left">
                  <tr>
                    <th className="px-4 py-2 font-semibold">Kenmerk</th>
                    <th className="px-4 py-2 font-semibold">Specificatie</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const rows = normalizeSpecs(product);
                    return rows.map((s, i) => (
                      <tr key={`${s.label}-${i}`} className={i % 2 === 0 ? "bg-white" : "bg-muted/30"}>
                        <td className="px-4 py-2 text-text-secondary">{s.label}</td>
                        <td className="px-4 py-2 font-medium">{s.value}</td>
                      </tr>
                    ));
                  })()}
                </tbody>

              </table>
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-16">
            <h2 className="text-xl font-bold">Gerelateerde producten</h2>
            {/* Mobile (1) + tablet (2): snap carousel. Desktop: 4-col grid */}
            <div className="mt-4 -mx-4 flex snap-x snap-mandatory scroll-smooth scroll-px-4 items-start gap-3 overflow-x-auto overscroll-x-contain px-4 pb-2 lg:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {related.map((p) => (
                <div key={p.slug} className="w-[calc(100vw-2rem)] shrink-0 snap-start sm:w-[calc(50%-0.5rem)]">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
            <div className="mt-4 hidden gap-4 lg:grid lg:grid-cols-4">
              {related.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          </section>
        )}

        {/* Recently viewed */}
        {recentProducts.length > 0 && (
          <section className="mt-16">
            <h2 className="text-xl font-bold">Recent bekeken</h2>
            <div className="mt-4 -mx-4 flex snap-x snap-mandatory scroll-smooth scroll-px-4 items-start gap-3 overflow-x-auto overscroll-x-contain px-4 pb-2 lg:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {recentProducts.map((p) => (
                <div key={p.slug} className="w-[calc(100vw-2rem)] shrink-0 snap-start sm:w-[calc(50%-0.5rem)]">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
            <div className="mt-4 hidden gap-4 lg:grid lg:grid-cols-4">
              {recentProducts.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}
